import React, { useState, useEffect } from 'react';
import { Button, Flex, View, Checkbox, Text } from '@smarterservices/smarter-ui';
import { BlockComponentProps, WizardBlock as WizardBlockType, Replacements } from '../../types';
import BlockRenderer from '../../BlockRenderer';

/**
 * WizardBlock component that breaks content into steps based on wizard_step blocks
 * and provides navigation between steps.
 *
 * @param {BlockComponentProps<WizardBlockType>} props - Component props
 * @returns {React.ReactElement} Rendered component
 */
export interface WizardBlockProps {
  block: WizardBlockType;
  onWizardComplete?: () => void;
  replacements?: Replacements;
}

export const WizardBlock = ({ 
  block,
  onWizardComplete,
  replacements
}: WizardBlockProps) => {
  
  const [currentStep, setCurrentStep] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);

  // If no steps, render nothing
  if (!block.elements || block.elements.length === 0) return null;
  
  const isLastStep = currentStep === block.elements.length - 1;
  const currentStepBlock = block.elements[currentStep];
  const buttonText = isLastStep
    ? (currentStepBlock.nextButtonText || 'Complete')
    : (currentStepBlock.nextButtonText || 'Next');
  const requiresAcknowledgement = !!currentStepBlock.acknowledgementText;
  
  // Reset acknowledgement when step changes
  useEffect(() => {
    setAcknowledged(false);
  }, [currentStep]);
  
  const handleNext = () => {
    if (currentStep < block.elements.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // On the last step
      if (onWizardComplete) {
        onWizardComplete();
      }
    }
  };
  
  return (
    <View as="div" className="block-wizard" margin={block.margin || "none"}>
      <View as="div" className="block-wizard-content">
        <BlockRenderer 
          blocks={currentStepBlock.elements} 
          // No need to pass replacements, they're available via context
        />
      </View>
      
      {requiresAcknowledgement && (
        <Flex
          direction="row"
          alignItems="center"
          margin="medium 0 0 0"
        >
          <View background="secondary" padding="small" borderRadius="medium" borderColor='primary' borderWidth="small">
            <Checkbox
              id={`acknowledge-step-${currentStep}`}
              label={<Text size="small">{currentStepBlock.acknowledgementText}</Text>}
              checked={acknowledged}
              onChange={() => setAcknowledged(!acknowledged)}
            />
          </View>
        </Flex>
      )}
      
      <Flex direction="row" justifyItems="end" margin="medium 0 0 0">
        {(!requiresAcknowledgement || acknowledged) && (
          <Button
            color="primary"
            onClick={handleNext}
          >
            {buttonText}
          </Button>
        )}
      </Flex>
    </View>
  );
};

export default WizardBlock;
